import type { Element, Faction } from './constants.ts';
import type { Dice } from './dice.ts';
import { dist2d, segmentIntersectsAabb, v3, type Vec3 } from './math.ts';
import {
  WALL_HEIGHT, WALL_THICKNESS, WALL_WIDTH,
} from './constants.ts';

/**
 * Placed effects — the first spells that put something into the world rather
 * than onto a player.
 *
 * Core owns these outright. The renderer mirrors them into meshes, and into
 * Rapier colliders for barriers that block bodies, exactly as it already mirrors
 * projectiles. What it does *not* do is decide anything: whether a spell got
 * past a wall is answered here, by geometry core can compute itself, because the
 * server has to reach the same verdict without the client's physics world.
 */
interface Placed {
  id: number;
  ownerId: string;
  faction: Faction;
  spellId: string;
  /** Seconds left. Culled at or below zero. */
  remaining: number;
}

// ─────────────────────────────────────────────────────────────────────────────
//  STORMS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A ground-targeted cylinder that ticks on everyone standing in it. Its dice are
 * rolled per tick rather than per cast, so the damage is what you take for
 * staying rather than what you take for being there when it lands.
 */
export interface Storm extends Placed {
  /**
   * How much this storm slows what stands in it, and for how long after leaving.
   *
   * Cold's equivalent of fire's burning: a storm you have to *leave* rather than
   * one you can walk through and shake off.
   */
  chill?: number;
  chillSeconds?: number;
  element: Element;
  dice: Dice;
  /** Centre, on the floor. */
  pos: Vec3;
  radius: number;
  /** Vertical reach. Flying over one is a legitimate dodge. */
  height: number;
  /** Seconds until the next damage tick. */
  nextTick: number;
}

/** `pos` is a player's feet, which is what the controller writes back. */
export function insideStorm(storm: Storm, pos: Vec3): boolean {
  if (pos.y < storm.pos.y - 0.5) return false;
  if (pos.y > storm.pos.y + storm.height) return false;
  return dist2d(pos, storm.pos) <= storm.radius;
}

// ─────────────────────────────────────────────────────────────────────────────
//  BARRIERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every wall in the game, from Airwall to Wall of Ice, is this record with
 * different numbers. Barrier Law in the original is a six-spell ladder that is
 * really one behaviour with a permeability ramp, so it is modelled as one.
 *
 * `blocksBodies` is the only field the renderer acts on — it is what decides
 * whether a Rapier collider gets spawned. Everything else is decided in core.
 */
export interface Barrier extends Placed {
  element: Element;
  /** Centre of the slab. */
  pos: Vec3;
  /** Full extents. Walls are wide, tall and thin. */
  size: Vec3;
  /** Rotation about Y, radians. Walls face the caster's yaw. */
  yaw: number;
  /** Solid to walking. Given a Rapier collider on both ends, by `net/levelColliders.ts`. */
  blocksBodies: boolean;
  /** Stops projectiles. Independent of `blocksBodies` — Airwall does one, not the other. */
  blocksSpells: boolean;
  /** Movement multiplier while inside the slab. 1 is no impediment. */
  passSlow: number;
  /** Damage dealt once, on entering. */
  onPassDice?: Dice;
  /** Power drained once, on entering. */
  onPassPpDrain?: number;
  /**
   * Who is inside right now. Pass-through effects fire on the transition into
   * this list, not on an interval — the manual says "all passing through it take
   * damage", and a 0.6m slab crossed at running speed would dodge any interval
   * tick worth having.
   */
  occupants: string[];
}

/** The tier-varying half of a wall. Damage and drain come from the spell. */
export interface BarrierProfile {
  blocksBodies: boolean;
  blocksSpells: boolean;
  passSlow: number;
}

const solid = (): BarrierProfile => ({ blocksBodies: true, blocksSpells: true, passSlow: 1 });
const porous = (passSlow: number, blocksSpells = true): BarrierProfile =>
  ({ blocksBodies: false, blocksSpells, passSlow });

/**
 * Per-tier wall behaviour, indexed by tier − 1.
 *
 * Barrier Law is the interesting one. The original ladder — Airwall, Waterwall,
 * Woodwall, Earthwall, Icewall, Stonewall — is not six behaviours; it is one
 * behaviour whose permeability ramps, ending solid. Six originals into five
 * tiers means Icewall is the one dropped.
 */
export const BARRIER_PROFILES: Record<string, readonly BarrierProfile[]> = {
  airwall: [porous(0.5), porous(0.4), porous(0.3), porous(0.2), solid()],
  wall_of_ice: [solid(), solid(), solid(), solid(), solid()],
  // Passable and invisible to spells both ways. All it does is hurt to cross.
  wall_of_cold: Array.from({ length: 5 }, () => porous(1, false)),
  mana_wall: Array.from({ length: 5 }, () => porous(0.7)),
};

export const WALL_SIZE = (): Vec3 => v3(WALL_WIDTH, WALL_HEIGHT, WALL_THICKNESS);

/** A point in the barrier's own space, with the slab axis-aligned. */
function toLocal(b: Barrier, p: Vec3): Vec3 {
  const dx = p.x - b.pos.x;
  const dy = p.y - b.pos.y;
  const dz = p.z - b.pos.z;
  const c = Math.cos(-b.yaw);
  const sn = Math.sin(-b.yaw);
  return { x: dx * c - dz * sn, y: dy, z: dx * sn + dz * c };
}

/** `pos` is a player's feet, so the test is generous by half a body upward. */
export function insideBarrier(b: Barrier, pos: Vec3): boolean {
  const l = toLocal(b, { x: pos.x, y: pos.y + 0.9, z: pos.z });
  return (
    Math.abs(l.x) <= b.size.x / 2 &&
    Math.abs(l.y) <= b.size.y / 2 &&
    Math.abs(l.z) <= b.size.z / 2
  );
}

/** True when the segment crosses the slab. Faction is irrelevant: a wall you
 * put up stops your own shots too, which is the cost of putting it there. */
export function segmentHitsBarrier(b: Barrier, from: Vec3, to: Vec3): boolean {
  return segmentIntersectsAabb(
    toLocal(b, from),
    toLocal(b, to),
    { x: b.size.x / 2, y: b.size.y / 2, z: b.size.z / 2 },
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SIGNS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Arcanist floor traps. Team-coloured and dim, except Expulsion, which is white
 * for everyone and launches its own caster too.
 *
 * `triggerRadius` and `radius` are deliberately different numbers: the decal is
 * drawn to the trigger, so what you see is what you step on, while the blast can
 * still reach much further.
 */
export interface Sign extends Placed {
  element: Element;
  dice?: Dice;
  pos: Vec3;
  /** Where it arms. Matched exactly by the floor decal. */
  triggerRadius: number;
  /** Where its effect reaches, once armed. */
  radius: number;
  /** Expulsion arms for anyone, including the Arcanist who laid it. */
  triggersOnOwnFaction: boolean;
  /** Seconds before it will trigger at all. */
  armFor: number;
}

export function onSign(sign: Sign, pos: Vec3): boolean {
  if (Math.abs(pos.y - sign.pos.y) > 2.2) return false;
  return dist2d(pos, sign.pos) <= sign.triggerRadius;
}
